import React, { useEffect } from "react";
import { Redirect, Route, Switch, useHistory } from "react-router-dom";
import PrivateRoute from "./privateRoute";
import EventContainer from "../components/events/eventContainer";
import EventsList, { EsqueletoEventos } from "../pages/events/eventsList";
import Guide from "../pages/guide/guide";
import { Hub } from 'aws-amplify';
import AccountCointainer from "../components/account/accountCointainer";
import CognitoLanding from "../pages/auth/cognito";
import Nav from "../components/navigation/nav";
import Header from "../components/navigation/header";
import Footer from "../components/navigation/footer";
import StripeValidationBanner from "../components/generic/StripeValidationBanner";
import RevToastContainer from "../components/ds/RevToastContainer";
import { withOneTabEnforcer } from "../helpers/duplicatedTab";
import { useLocation } from "react-router-dom/cjs/react-router-dom.min";
import authManager from "../services/authManager";

// todo: move to env
const LOCAL_STORAGE_KEY = 'n30xp5jdk7a';

// Cromo común (raíl, cabecera, banner Stripe, footer) alrededor de cada pantalla privada.
// A nivel de módulo a propósito: si se creara dentro de RouterConfig, cada cambio de URL
// (también un ?q= del buscador) daría una identidad nueva a estos componentes y React
// remontaría toda la pantalla: la lista volvería a pedir datos y la cabecera perdería el foco.
// El cromo pinta también el arranque: mientras PrivateRoute resuelve la autenticación llega
// `authPending` y en el hueco de contenido va `Esqueleto` (o nada) en vez de la pantalla. Al
// resolverse, el árbol es el MISMO tipo en la MISMA posición, así que React no remonta ni el
// raíl ni la cabecera y el marcado del cromo sigue viviendo una sola vez.
const hoc = (Component, Esqueleto) => {
    const OnlyOneTabComponent = withOneTabEnforcer()(Component);
    return ({ authPending, ...props }) => (
        <>
            <RevToastContainer />
            <div className="rev-app">
                <Nav />
                <div className="rev-app__col">
                    <Header />
                    <StripeValidationBanner />
                    <div className="rev-app__content">
                        {authPending
                            ? (Esqueleto ? <Esqueleto /> : null)
                            : <OnlyOneTabComponent {...props} />}
                    </div>
                    <Footer />
                </div>
            </div>
        </>
    );
};

const WrappedEventContainer = hoc(EventContainer);
const WrappedAccountCointainer = hoc(AccountCointainer);
const WrappedEventsList = hoc(EventsList, EsqueletoEventos);
const WrappedGuide = hoc(Guide);

export const RouterConfig = () => {
    const history = useHistory();
    const location = useLocation();

    // ✅ Chequeo inicial y manejo de tokens URL simplificado
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                // Verificar si hay token en URL (para impersonación)
                const query = new URLSearchParams(location.search);
                const urlToken = query.get('token');
                // Evento de destino de la suplantación desde el admin: solo con token.
                const urlEvent = urlToken ? query.get('event') : null;

                if (urlToken) {
                    localStorage.setItem(LOCAL_STORAGE_KEY, urlToken);
                }

                // Usar AuthManager para verificar autenticación
                const isAuth = await authManager.isAuthenticated();
                
                if (isAuth && (location.pathname?.includes('signIn') || location.pathname?.includes('signUp'))) {
                    // Destino compuesto con prefijo fijo y id validado: sin open redirect.
                    history.replace(urlEvent && /^[A-Za-z0-9_-]{1,64}$/.test(urlEvent)
                        ? '/events/' + urlEvent + '/basic'
                        : '/events');
                } else if (!isAuth && !location.pathname?.includes('signIn') && !location.pathname?.includes('signUp')) {
                    history.replace('/signIn');
                }
            } catch (error) {
                console.error('[RouterConfig] Auth initialization failed:', error);
                localStorage.removeItem(LOCAL_STORAGE_KEY);
                if (!location.pathname?.includes('signIn') && !location.pathname?.includes('signUp')) {
                    history.replace('/signIn');
                }
            }
        };

        initializeAuth();
        // eslint-disable-next-line
    }, [location.pathname]);

    // ✅ Hub listener simplificado - maneja eventos de auth
    useEffect(() => {
        const listener = async (data) => {
            const { event } = data.payload;

            console.log('event', event);
            
            // Eventos que requieren redirección a signIn
            const signOutEvents = ['signOut', 'signIn_failure', 'autoSignIn_failure', 'tokenRefresh_failure'];
            
            if (signOutEvents.includes(event)) {
                localStorage.removeItem(LOCAL_STORAGE_KEY);
                if (!location.pathname?.includes('signIn') && !location.pathname?.includes('signUp')) {
                    history.replace('/signIn');
                }
            } else if (event === 'signIn' && (location.pathname?.includes('signIn') || location.pathname?.includes('signUp'))) {
                history.replace('/events');
            }
            
        };

        const hubListener = Hub.listen('auth', listener);
        return () => hubListener();
    }, [history, location.pathname]);

    return (
        <Switch>
            <Route path="/signIn" component={CognitoLanding} />
            <Route path="/signUp" component={CognitoLanding} />
            <PrivateRoute path="/events/:id/:mode" component={WrappedEventContainer} />
            <PrivateRoute path="/events/new" component={WrappedEventContainer} />
            <PrivateRoute path="/account/:mode/:redirect" component={WrappedAccountCointainer} />
            <PrivateRoute path="/events" component={WrappedEventsList} />
            <PrivateRoute path="/guide/:tab?" component={WrappedGuide} />
            <Redirect from="/" to="/events" />
        </Switch>
    );
};
